import React, { Component, Fragment } from 'react';
import { Form, Input, Label } from 'reactstrap';
import propTypes from 'prop-types';
import get from 'lodash/get';

import ConfirmationModal from 'app/ConfirmationModal/components/ConfirmationModal';
import ContextMenu from 'app/ContextMenu/components/ContextMenu';
import DragAndDropList from 'app/DragAndDropList/container';
import EditComponentModal from 'app/EditComponentModal/components/EditComponentModal';
import { duplicateComponent } from 'utils/factory';

const REMOVE = 'REMOVE';
const EDIT = 'EDIT';

export const actions = [
  { text: 'Remove', action: REMOVE },
  { text: 'Edit', action: EDIT }
];

/**
 * This component can be used to show a list of children components with
 * common actions (Remove, Edit Original) actions
 * @returns {Component} returns component showing list of children components with action.
 */
export default class ChildrenComponentsList extends Component {
  static propTypes = {
    // own props
    beforeRemove: propTypes.func.isRequired,
    experimentParameters: propTypes.array.isRequired,
    isExperimentComponent: propTypes.bool,
    isReadonly: propTypes.bool,
    itemNameField: propTypes.string,
    items: propTypes.arrayOf(propTypes.shape()),
    lanId: propTypes.string,
    onChange: propTypes.func.isRequired,
    openComponentForEditing: propTypes.func.isRequired,

    // actions
    checkStatusAndSwitchToComponent: propTypes.func.isRequired,
    clearComponentUsage: propTypes.func.isRequired,
    getComponentUsage: propTypes.func.isRequired,
    notifySuccess: propTypes.func.isRequired,
    unboundVisitComponent: propTypes.func.isRequired,
    unboundVisitPage: propTypes.func.isRequired
  };

  static defaultProps = {
    isExperimentComponent: false,
    isReadonly: true,
    itemNameField: 'ComponentName',
    items: [],
    lanId: ''
  };

  constructor(props) {
    super(props);

    this.state = {
      isModalOpen: false,
      selectedComponentIndex: undefined,
      showRemoveComponentConfirmation: false
    };

    this.cancelClick = this.cancelClick.bind(this);
    this.createACopyClicked = this.createACopyClicked.bind(this);
    this.editOriginalClicked = this.editOriginalClicked.bind(this);
    this.onActionSelect = this.onActionSelect.bind(this);
    this.removeComponent = this.removeComponent.bind(this);
    this.changeItemsOrder = this.changeItemsOrder.bind(this);
    this.toggleRemoveComponentConfirmation = this.toggleRemoveComponentConfirmation.bind(this);
  }

  /**
   * handle variant changes
   * @param {object} event - input event with the new variant value
   * @param {number} index - index of the component with a variant change
   */
  handleVariantChange(event, index) {
    const { items, onChange } = this.props;
    const variant = event.target.value;
    const variants = items.map((obj, i) => (index === i ? { ...obj, variant } : obj));
    onChange(variants);
  }

  /**
   * execute selected action in selected item
   * @param {string} action - selected action
   * @param {string} index - index of the item on which action is selected
   */
  onActionSelect(action, index) {
    const {
      beforeRemove,
      getComponentUsage,
      isExperimentComponent,
      items,
      openComponentForEditing
    } = this.props;
    this.setState({ selectedComponentIndex: index });

    switch (action) {
      case REMOVE: {
        if (beforeRemove()) {
          this.toggleRemoveComponentConfirmation();
        }
        break;
      }
      case EDIT: {
        const component = isExperimentComponent ? items[index].data : items[index];
        const { Id, IsMaster, UsedInMultiplePlaces } = component;

        // Open inline editor pane if
        // 1. component is not master and
        // 2. component is not used on multiple places (page or container component)
        // else open the component usage modal where user can edit original or can create a copy.
        if (!IsMaster && !UsedInMultiplePlaces) {
          openComponentForEditing(component);
        } else {
          getComponentUsage(Id);
          this.toggleEditComponentModal();
        }
        break;
      }
      default:
        break;
    }
  }

  /**
   * toggles the modal and dispatches action to clear usage data
   */
  cancelClick() {
    const { clearComponentUsage } = this.props;
    this.toggleEditComponentModal();
    clearComponentUsage();
  }

  /**
   * dispatches required actions for Edit original clicked.
   */
  editOriginalClicked() {
    const { selectedComponentIndex } = this.state;
    const { isExperimentComponent, items } = this.props;
    const item = items[selectedComponentIndex];
    const component = isExperimentComponent ? item.data : item;
    const { clearComponentUsage, checkStatusAndSwitchToComponent } = this.props;
    this.toggleEditComponentModal();
    clearComponentUsage();
    checkStatusAndSwitchToComponent(component.Id, component.Version);
  }

  /**
   * dispatches required actions to create a copy.
   */
  createACopyClicked() {
    const { selectedComponentIndex } = this.state;
    const {
      clearComponentUsage,
      isExperimentComponent,
      items,
      lanId,
      notifySuccess,
      onChange,
      openComponentForEditing
    } = this.props;
    this.toggleEditComponentModal();
    clearComponentUsage();
    const item = items[selectedComponentIndex];
    const component = isExperimentComponent ? item.data : item;
    const duplicatedComponent = duplicateComponent({ component, lanId });
    const newList = [...items];
    newList[selectedComponentIndex] = isExperimentComponent
      ? { data: duplicatedComponent, variant: item.variant }
      : duplicatedComponent;
    onChange(newList);
    openComponentForEditing(duplicatedComponent);
    notifySuccess('Component has been replaced with a copy.');
  }

  /**
   * toggles component usage modal
   */
  toggleEditComponentModal() {
    this.setState(ps => ({ isModalOpen: !ps.isModalOpen }));
  }

  /**
   * toggles remove component confirmation modal
   */
  toggleRemoveComponentConfirmation() {
    this.setState(ps => ({
      showRemoveComponentConfirmation: !ps.showRemoveComponentConfirmation
    }));
  }

  /**
   * removes selected component from the list
   * @param {string} index - index of the item on which remove action is selected
   */
  removeComponent(index) {
    const {
      isExperimentComponent,
      itemNameField,
      items,
      notifySuccess,
      onChange
    } = this.props;
    const newList = items.filter((t, i) => index !== i);
    const removedComponent = isExperimentComponent ? items[index].data : items[index];
    onChange(newList);
    this.toggleRemoveComponentConfirmation();

    notifySuccess(`'${get(removedComponent, itemNameField, '')}' is removed.`);
  }

  /**
   * if the original variant assigned in experiment creator, provide it as an option
   * @param {Array} parameters - values from experiment creator
   * @param {string} variant - value from form
   * @returns {Array} list of parameters
   */
  includeOriginalVariant(parameters, variant) {
    if (parameters.includes(variant)) {
      return parameters;
    }

    return parameters.concat(variant);
  }

  /**
   * Repositions the items in the array
   * @param {number} dragIndex - index number of the dragged item in the array
   * @param {number} hoverIndex - index number of the item dragged to in the array
   */
  changeItemsOrder(dragIndex, hoverIndex) {
    const { items, onChange } = this.props;
    const componentList = [...items];
    const dragCard = componentList[dragIndex];
    componentList.splice(dragIndex, 1);
    componentList.splice(hoverIndex, 0, dragCard);
    onChange(componentList);
  }

  /**
   * render EditComponentModal
   * @returns {React.Component} component that opens modal on click
   */
  renderEditComponentModal() {
    const { unboundVisitComponent, unboundVisitPage } = this.props;
    const { isModalOpen } = this.state;
    return (
      <EditComponentModal
        data-test-id="pageComponentsList-editComponentModal"
        isOpen={isModalOpen}
        onCancelClick={this.cancelClick}
        onEditCopyClick={this.createACopyClicked}
        onEditOriginalClick={this.editOriginalClicked}
        unboundVisitComponent={unboundVisitComponent}
        unboundVisitPage={unboundVisitPage}
      />
    );
  }

  render() {
    const { experimentParameters, isExperimentComponent, items, isReadonly } = this.props;
    const components = isExperimentComponent ? items.map(item => item.data) : items;
    const { selectedComponentIndex, showRemoveComponentConfirmation } = this.state;
    const selectedComponent =
      selectedComponentIndex !== undefined ? components[selectedComponentIndex] : undefined;

    let parameters = [];

    if (!experimentParameters.length) {
      parameters = ['Default'];
    } else {
      parameters = experimentParameters;
    }
    const list = components.map((component, index) => (
      <div key={component.Id} className="d-flex justify-content-between">
        <div className="d-flex flex-column w-95">
          <span className="d-inline-block text-truncate" title={component.ComponentName}>
            {`${index + 1}. ${component.ComponentName}`}
          </span>
          {isExperimentComponent && (
            <Form inline>
              <Label className="font-weight-bold ml-4 mr-2">Parameter:</Label>
              <Input
                disabled={isReadonly}
                className="rounded"
                type="select"
                value={items[index].variant}
                onChange={e => this.handleVariantChange(e, index)}
                data-test-id={`experiment-form-item-${index}-variant-select`}
              >
                {this.includeOriginalVariant(parameters, items[index].variant).map(
                  (variant, index) => (
                    <option key={index} value={variant}>
                      {variant}
                    </option>
                  )
                )}
              </Input>
            </Form>
          )}
        </div>
        {!isReadonly && (
          <ContextMenu
            actions={actions}
            onActionSelect={action => this.onActionSelect(action, index)}
            isReadonly={isReadonly}
            popoverPlacement="left"
          />
        )}
      </div>
    ));
    return isReadonly ? (
      list
    ) : (
      <Fragment>
        <DragAndDropList
          cardClassName="px-2 py-1 mb-1"
          changeItemsOrder={this.changeItemsOrder}
        >
          {list}
        </DragAndDropList>
        <ConfirmationModal
          isOpen={showRemoveComponentConfirmation}
          onNoClick={this.toggleRemoveComponentConfirmation}
          onYesClick={() => this.removeComponent(selectedComponentIndex)}
          data-test-id="childrenComponentsList-removeComponentConfirmationModal"
        >
          <p>Are you sure, you want to remove this component from the list?</p>
          <strong>{get(selectedComponent, 'ComponentName', '')}</strong>
        </ConfirmationModal>
        {this.renderEditComponentModal()}
      </Fragment>
    );
  }
}
